import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from "@mui/material"
import { X } from "@phosphor-icons/react"
import React from "react"
import { DeviceOption } from "../../types/Common"

interface DeviceSelectionDialogProps {
  open: boolean
  onClose: () => void
  audioInputs: DeviceOption[]
  videoInputs: DeviceOption[]
  selectedAudioInput: string
  selectedVideoInput: string
  handleAudioInputChange: (deviceId: string) => void
  handleVideoInputChange: (deviceId: string) => void
}

const selectSx = {
  backgroundColor: "var(--color-background-bg-component)",
  color: "var(--color-text-text-title)",
  borderRadius: "var(--spacing-tokens-size-in-px-radius-radius-lg)",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "var(--color-border-border-component)",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "var(--color-border-border-component-active)",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "var(--color-border-border-brand)",
  },
  "& .MuiSvgIcon-root": { color: "var(--color-foreground-fg-secondary)" },
}

const menuProps = {
  slotProps: {
    paper: {
      sx: {
        backgroundColor: "var(--color-background-bg-component)",
        borderRadius: "var(--spacing-tokens-size-in-px-radius-radius-lg)",
        border: "1px solid var(--color-border-border-component)",
        marginTop: "var(--spacing-tokens-size-in-px-spacing-spacing-md)",
        "& .MuiMenuItem-root": {
          color: "var(--color-text-text-title)",
          "&:hover": {
            backgroundColor: "var(--color-background-bg-component-hover)",
          },
          "&.Mui-selected": {
            color: "var(--color-text-text-clicable)",
            backgroundColor: "var(--color-background-bg-secondary-action)",
            "&:hover": {
              backgroundColor:
                "var(--color-background-bg-secondary-action-hover)",
            },
          },
        },
      },
      onClick: (e: React.MouseEvent) => e.stopPropagation(),
    },
  },
}

const labelSx = {
  color: "var(--color-text-text-label)",
  mb: 1,
  fontWeight: 500,
}

export const DeviceSelectionDialog: React.FC<DeviceSelectionDialogProps> = ({
  open,
  onClose,
  audioInputs,
  videoInputs,
  selectedAudioInput,
  selectedVideoInput,
  handleAudioInputChange,
  handleVideoInputChange,
}) => {
  const handleCloseClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      onClick={(e) => e.stopPropagation()}
      slotProps={{
        paper: {
          sx: {
            backgroundColor: "var(--color-background-bg-page-contrast)",
            borderRadius: "var(--spacing-tokens-size-in-px-radius-radius-3xl)",
            minWidth: { xs: "90vw", sm: "400px" },
            maxWidth: "500px",
            border: "1px solid var(--color-border-border-divider)",
          },
          onClick: (e: React.MouseEvent) => e.stopPropagation(),
        },
      }}
      sx={{ "& .MuiBackdrop-root": { backgroundColor: "var(--call-scrim)" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          color: "var(--color-text-text-title)",
          pb: 1,
        }}
      >
        Device Settings
        <IconButton
          onClick={handleCloseClick}
          aria-label="Close device settings"
          sx={{
            color: "var(--color-foreground-fg-secondary)",
            "&:hover": {
              backgroundColor: "var(--color-background-bg-component-hover)",
            },
          }}
        >
          <X size={24} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={labelSx}>
            Microphone
          </Typography>
          <FormControl fullWidth>
            <Select
              value={selectedAudioInput}
              onChange={(e) => handleAudioInputChange(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              sx={selectSx}
              MenuProps={menuProps}
            >
              {audioInputs.map((device) => (
                <MenuItem key={device.deviceId} value={device.deviceId}>
                  {device.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box>
          <Typography variant="subtitle1" sx={labelSx}>
            Camera
          </Typography>
          <FormControl fullWidth>
            <Select
              value={selectedVideoInput}
              onChange={(e) => handleVideoInputChange(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              sx={selectSx}
              MenuProps={menuProps}
            >
              {videoInputs.map((device) => (
                <MenuItem key={device.deviceId} value={device.deviceId}>
                  {device.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
